import { z } from 'zod';

/**
 * Decision-class statements extracted from expert answer messages by the
 * inquiry-workflow pipeline and served by the statement-references API.
 *
 * `statementType`, `intent`, and `stance` are intentionally typed as
 * `string` rather than closed enums so that server-side enum additions
 * never break the client. UI formats type and intent uniformly
 * (snake_case → Sentence case) without per-value lookups. `stance` is
 * kept in the schema for forward compatibility but is not rendered in
 * v1.
 *
 * When extraction is skipped the message has no statement-references
 * entry, which UIs treat as "no statements extracted".
 *
 * `attachedStatementReferenceSchema` is the client-owned sibling. Each
 * entry binds one `ReferenceData` (resolved from a URL via the existing
 * format-references-batch path) to one `statementIndex` from the
 * pipeline-emitted statements. Pipeline output is read-only; attached
 * references are write-only by the message author.
 */

export const linkedQuestionSchema = z.object({
  /** Verbatim question text from the LMP message that this statement
   * answers. Rendered read-only inside the expanded card. */
  questionText: z.string(),
  /** Twilio SID of the message the linked question came from, or null
   * when the question has no stored message. Held for forward-compat
   * (e.g., future scroll-to-source behavior); not acted on in v1. */
  questionExternalMessageId: z.string().nullable(),
});

export type LinkedQuestion = z.infer<typeof linkedQuestionSchema>;

export const extractedStatementSchema = z.object({
  /** Normalized decision-class statement text. Render this in the
   * expanded card. */
  directStatement: z.string(),
  /** Server-supplied type tag (e.g. `treatment_recommendation`,
   * `monitoring_or_assessment`). Typed as `string` so unknown future
   * values still render. */
  statementType: z.string(),
  /** `definitive` | `conditional`. Not rendered in v1; held for
   * forward-compat. */
  stance: z.string(),
  /** `direct` | `clarifying`. Rendered as a badge alongside
   * `statementType`. */
  intent: z.string(),
  /** Server-known references already attached to this statement.
   * Held for forward-compat (the v1 bubble does not surface
   * reference-add affordances). */
  trialOrGuidelineRefs: z.array(z.string()),
  /** Indicates the most decision-relevant statement in the set.
   * Not rendered in v1 (no marker or reorder). */
  isPrimary: z.boolean(),
  /** 0-based inclusive char offset into the message body. `null` if
   * the quote could not be unambiguously located. */
  sourceStartIndex: z.number().nullable(),
  /** 0-based exclusive char offset into the message body. `null` if
   * the quote could not be unambiguously located. */
  sourceEndIndex: z.number().nullable(),
  /** Verbatim back-pointer(s) to LMP question(s) this statement
   * answers. May span multiple messages. */
  linkedQuestions: z.array(linkedQuestionSchema),
});

export type ExtractedStatement = z.infer<typeof extractedStatementSchema>;

export const extractedStatementSummarySchema = z.object({
  /** Ordered by `statement_index ASC`. */
  extractedStatements: z.array(extractedStatementSchema),
});

export type ExtractedStatementSummary = z.infer<typeof extractedStatementSummarySchema>;

/**
 * One client-attached reference bound to a single extracted statement.
 *
 * `statementIndex` is the position into the message's
 * `extractedStatements` array. `url` is the verbatim URL the author
 * supplied — v1 stores nothing else. Title / journal / authors / year
 * resolution is deferred until a future iteration adds the
 * format-references-batch wiring back; renderers fall back to the
 * URL hostname (or the URL itself) for display until then.
 *
 * Multiple entries MAY share the same `statementIndex` (a single
 * statement can cite several references). Display order at the bubble
 * level is determined by walking `extractedStatements` in index order
 * and entries within each statement in array order — see
 * `computeReferenceNumbering`.
 */
export const attachedStatementReferenceSchema = z.object({
  statementIndex: z.number().int().nonnegative(),
  url: z.string().url(),
});

export type AttachedStatementReference = z.infer<typeof attachedStatementReferenceSchema>;

/**
 * Author-owned editorial intent: "the author wants this statement to be
 * cited." Independent of whether a reference has actually been attached
 * — the author can mark a statement as needing citation before pasting
 * any URL.
 *
 * `statementIndex` is the position into the message's
 * `extractedStatements` array. `needsCitation` is a boolean intent.
 *
 * Stored by the statement-references API, which lets only the message
 * author write it and shows it to no one else. Missing entry
 * for a `statementIndex` MUST resolve at read time to
 * `needsCitation: false` (opt-in default).
 *
 * See `resolveNeedsCitation` and `applyCitationStatusChanges` for the
 * canonical read/write helpers.
 */
export const statementCitationStatusSchema = z.object({
  statementIndex: z.number().int().nonnegative(),
  needsCitation: z.boolean(),
});

export type StatementCitationStatus = z.infer<typeof statementCitationStatusSchema>;

/**
 * Author-owned per-statement free-text help request. The expert
 * describes an article they have in mind but can't locate a URL for;
 * Primum staff find it and attach the resolved URL. At most one entry
 * per statement. The entry is not auto-cleared on resolution — it
 * stays as audit until the author removes it.
 *
 * `createdAt` is null for requests saved before the store recorded a
 * timestamp.
 */
export const statementReferenceHelpRequestSchema = z.object({
  statementIndex: z.number().int().nonnegative(),
  description: z.string().min(1).max(500),
  createdAt: z.string().datetime().nullish(),
});

export type StatementReferenceHelpRequest = z.infer<typeof statementReferenceHelpRequestSchema>;

/**
 * A reference that can be rendered against a statement — either resolved
 * (has a URL the author or staff attached) or pending (the author typed a
 * description and Primum staff have not yet attached a URL).
 *
 * Pending entries render only in the author's view. The recipient (LMP)
 * sees a reference once it has a URL, never before. The merge helper
 * `mergeStatementReferences` enforces this contract.
 */
export type RenderableStatementReference =
  | {
      kind: 'resolved';
      statementIndex: number;
      url: string;
    }
  | {
      kind: 'pending';
      statementIndex: number;
      description: string;
      /** Optional — backend does not yet set this on persist. */
      createdAt?: string;
    };

export type StatementReferenceViewerRole = 'author' | 'recipient';

/**
 * Merge `attachedStatementReferences` (real references with URLs) and
 * `statementReferenceHelpRequests` (author-side pending descriptions)
 * into a single flat list of renderable references, gated by viewer role.
 *
 * Per-`statementIndex` rules:
 *
 * - **recipient**: only resolved entries flow through. Pending entries
 *   are hidden in every recipient-side surface (underline, chip, sheet,
 *   footer). The LMP sees a citation only after Primum staff has
 *   resolved the help-request by attaching a real URL — until then the
 *   help-request is operational state, not user-facing content.
 * - **author**: all resolved entries flow through. Pending entries also
 *   flow through regardless of whether resolved entries exist for the
 *   same `statementIndex` — the expert may intentionally add a help
 *   request alongside an already-resolved reference to request a second
 *   distinct reference. When neither exists for the `statementIndex`,
 *   nothing flows through.
 *
 * Output is ordered by `statementIndex` ascending so downstream
 * numbering walks remain deterministic across renders. Resolved entries are
 * accumulated via a Map for O(1) lookup during the pending-filter pass; the
 * final `merged.sort()` is the correctness guarantee for output order, not Map
 * iteration order.
 */
export function mergeStatementReferences({
  attached,
  helpRequests,
  viewerRole,
}: {
  attached: readonly AttachedStatementReference[] | undefined;
  helpRequests: readonly StatementReferenceHelpRequest[] | undefined;
  viewerRole: StatementReferenceViewerRole;
}): RenderableStatementReference[] {
  const merged: RenderableStatementReference[] = [];

  const attachedByStatement = new Map<number, AttachedStatementReference[]>();

  for (const entry of attached ?? []) {
    const bucket = attachedByStatement.get(entry.statementIndex) ?? [];

    bucket.push(entry);

    attachedByStatement.set(entry.statementIndex, bucket);
  }

  for (const [statementIndex, bucket] of attachedByStatement) {
    for (const entry of bucket) {
      merged.push({ kind: 'resolved', statementIndex, url: entry.url });
    }
  }

  if (viewerRole === 'author') {
    for (const entry of helpRequests ?? []) {
      merged.push({
        kind: 'pending',
        statementIndex: entry.statementIndex,
        description: entry.description,
        createdAt: entry.createdAt ?? undefined,
      });
    }
  }

  merged.sort((left, right) => left.statementIndex - right.statementIndex);

  return merged;
}

/**
 * Resolves the current help-request entry for a given `statementIndex`,
 * or `undefined` when none exists. Missing input or no matching entry
 * both resolve to `undefined`.
 *
 * On a duplicate `statementIndex` the last entry wins, matching
 * `applyHelpRequestUpsert` write semantics so reads and writes stay
 * consistent even if a malformed payload (or a future writer) ever
 * produces duplicates.
 */
export function resolveHelpRequestForStatement(
  helpRequests: readonly StatementReferenceHelpRequest[] | undefined,
  statementIndex: number
): StatementReferenceHelpRequest | undefined {
  if (!helpRequests) {
    return undefined;
  }

  let resolved: StatementReferenceHelpRequest | undefined;

  for (const entry of helpRequests) {
    if (entry.statementIndex === statementIndex) {
      resolved = entry;
    }
  }

  return resolved;
}

/**
 * Pure helper: upsert a `{ statementIndex, description, createdAt }`
 * entry into the help-requests array. If an entry for the same
 * `statementIndex` already exists it is replaced in place (no duplicate,
 * no extra entry); otherwise the new entry is appended. The result is
 * suitable to write whole through the statement-references API.
 *
 * Entries are kept in `statementIndex` ascending order for stable write
 * payloads. The order is NOT semantically significant — readers filter
 * by `statementIndex`.
 */
export function applyHelpRequestUpsert(
  current: readonly StatementReferenceHelpRequest[] | undefined,
  next: StatementReferenceHelpRequest
): StatementReferenceHelpRequest[] {
  const merged: StatementReferenceHelpRequest[] = [];

  let replaced = false;

  for (const entry of current ?? []) {
    if (entry.statementIndex === next.statementIndex) {
      if (!replaced) {
        merged.push(next);

        replaced = true;
      }

      continue;
    }

    merged.push(entry);
  }

  if (!replaced) {
    merged.push(next);
  }

  merged.sort((left, right) => left.statementIndex - right.statementIndex);

  return merged;
}

/**
 * Pure helper: remove the help-request entry for `statementIndex`.
 * Returns a new array; if no entry exists for that `statementIndex` the
 * returned array is a shallow copy of the input (callers can still
 * choose to write or skip).
 */
export function applyHelpRequestRemoval(
  current: readonly StatementReferenceHelpRequest[] | undefined,
  statementIndex: number
): StatementReferenceHelpRequest[] {
  if (!current) {
    return [];
  }

  return current.filter((entry) => entry.statementIndex !== statementIndex);
}

/**
 * Resolves the citation intent for a given `statementIndex`. Missing
 * entry → `false` (opt-in default). Always returns a definitive
 * boolean so callers don't have to handle `undefined`.
 *
 * On a duplicate `statementIndex` the last entry wins, matching
 * `applyCitationStatusChanges` write semantics so reads and writes
 * stay consistent even if a malformed payload (or a future writer)
 * ever produces duplicates.
 */
export function resolveNeedsCitation(
  statuses: readonly StatementCitationStatus[] | undefined,
  statementIndex: number
): boolean {
  if (!statuses) {
    return false;
  }

  let resolved = false;

  for (const entry of statuses) {
    if (entry.statementIndex === statementIndex) {
      resolved = entry.needsCitation;
    }
  }

  return resolved;
}

/**
 * Pure helper that applies a batch of `(statementIndex, needsCitation)`
 * changes to an existing `statementCitationStatuses` array and returns
 * the next array. Insert if missing, update if present. The result is
 * suitable to write whole through the statement-references API.
 *
 * Entries are kept in `statementIndex` ascending order for stable
 * write payloads (helps with equality checks and review of test
 * fixtures). The order is NOT semantically significant — readers
 * filter by `statementIndex`.
 */
export function applyCitationStatusChanges(
  current: readonly StatementCitationStatus[] | undefined,
  changes: ReadonlyMap<number, boolean>
): StatementCitationStatus[] {
  const merged = new Map<number, boolean>();

  for (const entry of current ?? []) {
    merged.set(entry.statementIndex, entry.needsCitation);
  }

  for (const [statementIndex, needsCitation] of changes) {
    merged.set(statementIndex, needsCitation);
  }

  const next: StatementCitationStatus[] = [];

  for (const [statementIndex, needsCitation] of merged) {
    next.push({ statementIndex, needsCitation });
  }

  next.sort((left, right) => left.statementIndex - right.statementIndex);

  return next;
}

/**
 * Client-side display overrides for known server `statementType` values
 * whose canonical names need softer copy in the UI. The pipeline still
 * emits the original snake_case value (e.g. `treatment_recommendation`)
 * — we only re-label here. Unknown values fall through to the generic
 * snake_case → Sentence case formatter.
 */
const STATEMENT_TYPE_DISPLAY_OVERRIDES: Record<string, string> = {
  treatment_recommendation: 'Treatment guidance',
};

/**
 * Formats a server-supplied `statementType` for display.
 *
 * Looks up the value in `STATEMENT_TYPE_DISPLAY_OVERRIDES` first so we
 * can soften specific server labels (e.g. `treatment_recommendation` is
 * surfaced as "Treatment guidance"). Anything not in the override map
 * falls through to a generic snake_case → Sentence case conversion so
 * future server values still render reasonably without a client patch.
 *
 * @example
 * formatStatementType('treatment_recommendation') // → 'Treatment guidance'
 * formatStatementType('monitoring_or_assessment') // → 'Monitoring or assessment'
 * formatStatementType('made_up_value')            // → 'Made up value'
 * formatStatementType('')                         // → ''
 */
export function formatStatementType(value: string): string {
  const override = STATEMENT_TYPE_DISPLAY_OVERRIDES[value];

  if (override !== undefined) {
    return override;
  }

  return formatSnakeCaseSentence(value);
}

function formatSnakeCaseSentence(value: string): string {
  if (!value) {
    return '';
  }

  const spaced = value.replace(/_+/g, ' ').trim();

  if (spaced.length === 0) {
    return '';
  }

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
